import React from 'react';
import { Plus, Edit, Trash2, ChevronDown, ChevronRight, Clock } from 'lucide-react';

const EventsAndNewsSection = ({ 
  events, 
  news, 
  expandedEvents, 
  expandedNews, 
  setExpandedEvents, 
  setExpandedNews,
  setEditingEvent,
  setEventForm,
  setShowEventModal,
  handleEditEvent,
  handleDeleteEvent,
  setEditingNews,
  setNewsForm,
  setShowNewsModal,
  handleEditNews,
  handleDeleteNews,
  toggleExpanded,
  // Modal props
  showEventModal,
  editingEvent,
  eventForm,
  eventLoading,
  handleEventSubmit,
  handleEventInputChange,
  showNewsModal,
  editingNews,
  newsForm,
  newsLoading,
  handleNewsSubmit,
  handleNewsInputChange
}) => {
  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Manage Events & News</h2>
      </div>

      {/* Events Section */}
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center space-y-4 sm:space-y-0">
          <button 
            onClick={() => {
              setEditingEvent(null);
              setEventForm({
                title: '',
                description: '',
                date: '',
                time: '',
                location: ''
              });
              setShowEventModal(true);
            }}
            className="sm:w-[186px] justify-center bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center text-sm sm:text-base">
            <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Add Event
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4">
            <h3 className="text-lg font-semibold">Events</h3>
          </div>
          {events.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-gray-600 mb-2">No event</h4>
              <p className="text-gray-500">No event has been added yet</p>
            </div>
            ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                        Location
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {events.map((event) => (
                      <tr key={event._id}>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{event.title}</div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {(() => {
                              const hasDate = event.date;
                              const hasTime = event.time;
                              
                              if (hasDate && hasTime) {
                                return `${new Date(event.date).toISOString().split('T')[0]}, ${event.time}`;
                              } else if (hasDate) {
                                return new Date(event.date).toISOString().split('T')[0];
                              } else if (hasTime) {
                                return event.time;
                              } else {
                                return '--';
                              }
                            })()}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{event.location || '--'}</div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleEditEvent(event)}
                              className="text-blue-600 hover:text-blue-900 p-1">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteEvent(event._id)}
                              className="text-red-600 hover:text-red-900 p-1">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Expandable List View */}
              <div className="md:hidden">
                {events.map((event) => {
                  const isExpanded = expandedEvents.has(event._id);
                  return (
                    <div key={event._id} className="border-b border-gray-200 last:border-b-0">
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleExpanded(event._id, expandedEvents, setExpandedEvents)}
                      >
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900">{event.title}</h3>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="flex space-x-1">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditEvent(event);
                              }}
                              className="text-blue-600 hover:text-blue-900 p-1">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteEvent(event._id);
                              }}
                              className="text-red-600 hover:text-red-900 p-1">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-4 pb-4 space-y-2">
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time:</span>
                            <p className="text-sm text-gray-900 mt-1">
                              {(() => {
                                const hasDate = event.date;
                                const hasTime = event.time;
                                
                                if (hasDate && hasTime) {
                                  return `${new Date(event.date).toISOString().split('T')[0]}, ${event.time}`;
                                } else if (hasDate) {
                                  return new Date(event.date).toISOString().split('T')[0];
                                } else if (hasTime) {
                                  return event.time;
                                } else {
                                  return '--';
                                }
                              })()}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Location:</span>
                            <p className="text-sm text-gray-900 mt-1">{event.location || '--'}</p>
                          </div>
                          {event.description && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Description:</span>
                              <p className="text-sm text-gray-900 mt-1">{event.description}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* News Section */}
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center space-y-4 sm:space-y-0">
          <button 
            onClick={() => {
              setEditingNews(null);
              setNewsForm({
                title: '',
                content: '',
                isPublished: false
              });
              setShowNewsModal(true);
            }}
            className="sm:w-[186px] justify-center bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center text-sm sm:text-base">
            <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Add News Article
          </button>
        </div>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4">
            <h3 className="text-lg font-semibold">News</h3>
          </div>
          {news.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-gray-600 mb-2">No news article</h4>
              <p className="text-gray-500">No news article has been added yet</p>
            </div>
            ) : (
            <div>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {news.map((article) => (
                      <tr key={article._id}>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{article.title}</div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{new Date(article.createdAt).toLocaleDateString()}</div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            article.isPublished ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {article.isPublished ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleEditNews(article)}
                              className="text-blue-600 hover:text-blue-900 p-1">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteNews(article._id)}
                              className="text-red-600 hover:text-red-900 p-1">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Expandable List View */}
              <div className="md:hidden">
                {news.map((article) => {
                  const isExpanded = expandedNews.has(article._id);
                  return (
                    <div key={article._id} className="border-b border-gray-200 last:border-b-0">
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleExpanded(article._id, expandedNews, setExpandedNews)}
                      >
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900">{article.title}</h3>
                          <div className="mt-1">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              article.isPublished ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {article.isPublished ? 'Published' : 'Draft'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="flex space-x-1">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditNews(article);
                              }}
                              className="text-blue-600 hover:text-blue-900 p-1">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteNews(article._id);
                              }}
                              className="text-red-600 hover:text-red-900 p-1">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-4 pb-4 space-y-2">
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created Date:</span>
                            <p className="text-sm text-gray-900 mt-1">{new Date(article.createdAt).toLocaleDateString()}</p>
                          </div>
                          {article.content && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Content:</span>
                              <p className="text-sm text-gray-900 mt-1 line-clamp-3">{article.content}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md mx-auto max-h-[97%] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{editingEvent ? 'Edit Event' : 'Add New Event'}</h3>
            <form onSubmit={handleEventSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={eventForm.title}
                  onChange={handleEventInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={eventForm.description}
                  onChange={handleEventInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Enter event description (optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  name="date"
                  value={eventForm.date}
                  onChange={handleEventInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <input
                  type="text"
                  name="time"
                  value={eventForm.time}
                  onChange={handleEventInputChange}
                  placeholder={eventForm.date ? "Enter time (e.g., 2:30 PM or 14:30)" : "Select a date first"}
                  disabled={!eventForm.date}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    !eventForm.date ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
                  }`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={eventForm.location}
                  onChange={handleEventInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={eventLoading}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {eventLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingEvent ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingEvent ? 'Update Event' : 'Create Event'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEventModal(false);
                    setEditingEvent(null);
                    setEventForm({
                      title: '',
                      description: '',
                      date: '',
                      time: '',
                      location: ''
                    });
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* News Modal */}
      {showNewsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md mx-auto max-h-[97%] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{editingNews ? 'Edit Article' : 'Add New Article'}</h3>
            <form onSubmit={handleNewsSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={newsForm.title}
                  onChange={handleNewsInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                <textarea
                  name="content"
                  value={newsForm.content}
                  onChange={handleNewsInputChange}
                  rows="6"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Enter article content (optional)"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isPublished"
                  id="isPublished"
                  checked={newsForm.isPublished}
                  onChange={handleNewsInputChange}
                  className="mr-2"
                />
                <label htmlFor="isPublished" className="text-sm font-medium text-gray-700">
                  Publish immediately
                </label>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={newsLoading}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {newsLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingNews ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingNews ? 'Update Article' : 'Create Article'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewsModal(false);
                    setEditingNews(null);
                    setNewsForm({
                      title: '',
                      content: '',
                      isPublished: false
                    });
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsAndNewsSection;